import {
  createContext,
  startTransition,
  useEffect,
  useMemo,
  type ReactNode,
  useState,
  useCallback,
  useRef,
  useContext,
} from 'react'

import { io, type Socket } from 'socket.io-client'

import { useAuthToken } from '@tribeplatform/react-sdk/hooks'

import { IdentifyRequest } from './constants.js'
import { IdentifyClientPayload } from './types.js'

interface Props {
  networkId: string
  config: {
    endPoint: string
    path: string
    namespacePrefix: string
    enabled: boolean
  }
  onSocketError?: (...args: unknown[]) => void
  onDebug?: (event: string, payload: unknown) => void
  onIdentify?: (error: unknown, response: unknown) => void
  children: ReactNode
}

export type SocketContextType = {
  socket: Socket
  connected: boolean
  identified: boolean
}

const SocketContext = createContext<SocketContextType>(null)

export const SocketProvider = ({
  networkId,
  children,
  config,
  onSocketError,
  onDebug,
  onIdentify,
}: Props) => {
  const { endPoint, path, namespacePrefix } = config
  const namespace = `${namespacePrefix}/${networkId}`

  const socketUrl = `${endPoint}${namespace}`

  const socketRef = useRef<Socket>(
    io(socketUrl, {
      autoConnect: false,
      path,
      transports: ['websocket'],
    }),
  )

  const [isConnected, setIsConnected] = useState(false)
  const [isIdentified, setIsIdentified] = useState(false)

  const {
    data: { accessToken },
  } = useAuthToken()

  const socket = socketRef.current

  /**
   * Effects are structured and decoupled in a way that prevents their re-execution on unrelated dependency changes.
   *
   * Socket events can fire while React is still hydrating a dehydrated Suspense
   * boundary, which would otherwise force the boundary to switch to client
   * rendering. Wrapping the resulting state updates in `startTransition` marks
   * them non-urgent so hydration can finish first.
   */
  useEffect(() => {
    socket.io.on('error', onSocketError)
    const handleSocketConnection = () => {
      startTransition(() => {
        setIsConnected(true)
      })
    }
    const handleSocketDisconnection = () => {
      startTransition(() => {
        setIsConnected(false)
        setIsIdentified(false)
      })
    }

    socket.on('connect', handleSocketConnection)
    socket.on('disconnect', handleSocketDisconnection)
    if (onDebug) {
      socket.onAny(onDebug)
    }

    return () => {
      socket.off('connect', handleSocketConnection)
      socket.off('disconnect', handleSocketDisconnection)
      if (onDebug) {
        socket.onAny(onDebug)
      }
    }
  }, [onDebug, onSocketError, socket])

  const identify = useCallback(async () => {
    try {
      const payload: IdentifyClientPayload = {
        token: accessToken,
      }
      const response = await socket.emitWithAck(IdentifyRequest, payload)
      if (onIdentify) {
        onIdentify(null, response)
      }
      startTransition(() => {
        setIsIdentified(response.status === 'ok')
      })
    } catch (error) {
      startTransition(() => {
        setIsIdentified(false)
      })
      if (onIdentify) {
        onIdentify(error, null)
      }
    }
  }, [socket, accessToken, onIdentify])

  useEffect(() => {
    if (!config.enabled) {
      return
    }

    if (isConnected) {
      identify()
    }
  }, [config.enabled, isConnected, identify])

  useEffect(() => {
    if (!config.enabled) {
      return
    }

    socket.connect()
  })

  const contextValue = useMemo<SocketContextType>(
    () => ({
      socket,
      connected: isConnected,
      identified: isIdentified,
    }),
    [socket, isConnected, isIdentified],
  )

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  )
}

export const useSocket = () => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider')
  }

  return context
}
